// src/context.ts
import { inspect } from "@dxos/node-std/util";
import { StackTrace } from "@dxos/debug";
import { log } from "@dxos/log";
import { safeInstanceof } from "@dxos/util";

// src/context-disposed-error.ts
var ContextDisposedError = class extends Error {
  constructor() {
    super("Context disposed.");
  }
};

// src/context.ts
var __dxlog_file = "/__w/dxos/dxos/packages/common/context/src/context.ts";
function _ts_decorate(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
var DEBUG_LOG_DISPOSE = false;
var MAX_SAFE_DISPOSE_CALLBACKS = 300;
var DEFAULT_ERROR_HANDLER = (error, ctx) => {
  if (error instanceof ContextDisposedError) {
    return;
  }
  void ctx.dispose();
  throw error;
};
var CONTEXT_FLAG_IS_DISPOSED = 1 << 0;
var CONTEXT_FLAG_LEAK_DETECTED = 1 << 1;
var Context = class _Context {
  static default() {
    return new _Context();
  }
  #disposeCallbacks = [];
  #name = void 0;
  #parent = void 0;
  #attributes;
  #onError;
  #flags = 0;
  #disposePromise = void 0;
  #signal = void 0;
  maxSafeDisposeCallbacks = MAX_SAFE_DISPOSE_CALLBACKS;
  constructor(params = {}, callMeta) {
    this.#name = getContextName(params, callMeta);
    this.#parent = params.parent;
    this.#attributes = params.attributes ?? {};
    this.#onError = params.onError ?? DEFAULT_ERROR_HANDLER;
  }
  get #isDisposed() {
    return !!(this.#flags & CONTEXT_FLAG_IS_DISPOSED);
  }
  set #isDisposed(value) {
    this.#flags = value ? this.#flags | CONTEXT_FLAG_IS_DISPOSED : this.#flags & ~CONTEXT_FLAG_IS_DISPOSED;
  }
  get #leakDetected() {
    return !!(this.#flags & CONTEXT_FLAG_LEAK_DETECTED);
  }
  set #leakDetected(value) {
    this.#flags = value ? this.#flags | CONTEXT_FLAG_LEAK_DETECTED : this.#flags & ~CONTEXT_FLAG_LEAK_DETECTED;
  }
  get disposed() {
    return this.#isDisposed;
  }
  get disposeCallbacksLength() {
    return this.#disposeCallbacks.length;
  }
  get signal() {
    if (this.#signal) {
      return this.#signal;
    }
    const controller = new AbortController();
    this.#signal = controller.signal;
    this.onDispose(() => controller.abort());
    return this.#signal;
  }
  /**
  * Schedules a callback to run when the context is disposed.
  * May be async, in this case the disposer might choose to wait for all resource to released.
  * Throwing an error inside the callback will result in the error being logged, but not re-thrown.
  *
  * NOTE: Will call the callback immediately if the context is already disposed.
  *
  * @returns A function that can be used to remove the callback from the dispose list.
  */
  onDispose(callback) {
    if (this.#isDisposed) {
      void (async () => {
        try {
          await callback();
        } catch (error) {
          log.catch(error, {
            context: this.#name
          }, { "~LogMeta": "~LogMeta", F: __dxlog_file, L: 92, S: this });
        }
      })();
    }
    this.#disposeCallbacks.push(callback);
    if (this.#disposeCallbacks.length > this.maxSafeDisposeCallbacks && !this.#leakDetected) {
      this.#leakDetected = true;
      const callSite = new StackTrace().getStackArray(1)[0].trim();
      log.warn("Context has a large number of dispose callbacks (this might be a memory leak).", {
        context: this.#name,
        callSite,
        count: this.#disposeCallbacks.length
      }, { "~LogMeta": "~LogMeta", F: __dxlog_file, L: 102, S: this });
    }
    return () => {
      const index = this.#disposeCallbacks.indexOf(callback);
      if (index !== -1) {
        this.#disposeCallbacks.splice(index, 1);
      }
    };
  }
  /**
  * Runs all dispose callbacks.
  * Callbacks are run in the reverse order they were added.
  * This function never throws.
  * It is safe to ignore the returned promise if the caller does not wish to wait for callbacks to complete.
  * Disposing context means that onDispose will throw an error and any errors raised will be logged and not propagated.
  * @returns true if there were no errors during the dispose process.
  */
  async dispose(throwOnError = false) {
    if (this.#disposePromise) {
      return this.#disposePromise;
    }
    this.#isDisposed = true;
    let resolveDispose;
    const promise = new Promise((resolve) => {
      resolveDispose = resolve;
    });
    this.#disposePromise = promise;
    const callbacks = Array.from(this.#disposeCallbacks).reverse();
    this.#disposeCallbacks.length = 0;
    if (DEBUG_LOG_DISPOSE) {
      log("disposing", {
        context: this.#name,
        count: callbacks.length
      }, { "~LogMeta": "~LogMeta", F: __dxlog_file, L: 140, S: this });
    }
    let i = 0;
    let clean = true;
    const errors = [];
    for (const callback of callbacks) {
      try {
        await callback();
        i++;
      } catch (err) {
        clean = false;
        if (throwOnError) {
          errors.push(err);
        } else {
          log.catch(err, {
            context: this.#name,
            callback: i,
            count: callbacks.length
          }, { "~LogMeta": "~LogMeta", F: __dxlog_file, L: 157, S: this });
        }
      }
    }
    if (errors.length > 0) {
      throw new AggregateError(errors);
    }
    resolveDispose(clean);
    if (DEBUG_LOG_DISPOSE) {
      log("disposed", {
        context: this.#name
      }, { "~LogMeta": "~LogMeta", F: __dxlog_file, L: 170, S: this });
    }
    return clean;
  }
  /**
  * Raise the error inside the context.
  * The error will be propagated to the error handler.
  * IF the error handler is not set, the error will dispose the context and cause an unhandled rejection.
  */
  raise(error) {
    if (this.#isDisposed) {
      return;
    }
    try {
      this.#onError(error, this);
    } catch (err) {
      void Promise.reject(err);
    }
  }
  derive({ onError, attributes } = {}) {
    const newCtx = new _Context({
      parent: this,
      // TODO(dmaretskyi): Optimize to not require allocating a new closure for every context.
      onError: async (error) => {
        if (!onError) {
          this.raise(error);
        } else {
          try {
            await onError(error, this);
          } catch {
            this.raise(error);
          }
        }
      },
      attributes
    });
    const clearDispose = this.onDispose(() => newCtx.dispose());
    newCtx.onDispose(clearDispose);
    return newCtx;
  }
  getAttribute(key) {
    if (key in this.#attributes) {
      return this.#attributes[key];
    }
    if (this.#parent) {
      return this.#parent.getAttribute(key);
    }
    return void 0;
  }
  [Symbol.toStringTag] = "Context";
  [inspect.custom] = () => this.toString();
  toString() {
    return `Context(${this.#isDisposed ? "disposed" : "active"})`;
  }
  async [Symbol.asyncDispose]() {
    await this.dispose();
  }
};
Context = _ts_decorate([
  safeInstanceof("Context")
], Context);
var getContextName = (params, callMeta) => {
  if (params.name) {
    return params.name;
  }
  if (callMeta?.F?.length) {
    const pathSegments = callMeta?.F.split("/");
    return `${pathSegments[pathSegments.length - 1]}#${callMeta?.L ?? 0}`;
  }
  return void 0;
};

// src/promise-utils.ts
var rejectOnDispose = (ctx, error = new ContextDisposedError()) => new Promise((resolve, reject) => {
  ctx.onDispose(() => reject(error));
});
var cancelWithContext = (ctx, promise) => {
  let clearDispose;
  return Promise.race([
    promise,
    new Promise((resolve, reject) => {
      clearDispose = ctx.onDispose(() => reject(new ContextDisposedError()));
    })
  ]).finally(() => clearDispose?.());
};

// src/resource.ts
import "@hazae41/symbol-dispose-polyfill";
import { throwUnhandledError } from "@dxos/util";
var LifecycleState = /* @__PURE__ */ (function(LifecycleState2) {
  LifecycleState2["CLOSED"] = "CLOSED";
  LifecycleState2["OPEN"] = "OPEN";
  LifecycleState2["ERROR"] = "ERROR";
  return LifecycleState2;
})({});
var CLOSE_RESOURCE_ON_UNHANDLED_ERROR = false;
var Resource = class {
  #lifecycleState = "CLOSED";
  #openPromise = null;
  #closePromise = null;
  /**
  * Managed internally by the resource.
  * Recreated on close.
  * Errors are propagated to the `_catch` method and the parent context.
  */
  #internalCtx = this.#createContext();
  /**
  * Context that is used to bubble up errors that are not handled by the resource.
  * Provided in the open method.
  */
  #parentCtx = this.#createParentContext();
  /**
  * ```ts
  * await using resource = new Resource();
  * await resource.open();
  * ```
  * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/using
  */
  async [Symbol.asyncDispose]() {
    await this.close();
  }
  get #name() {
    return Object.getPrototypeOf(this).constructor.name;
  }
  get isOpen() {
    return this.#lifecycleState === "OPEN" && this.#closePromise == null;
  }
  get _lifecycleState() {
    return this.#lifecycleState;
  }
  get _ctx() {
    return this.#internalCtx;
  }
  /**
  * To be overridden by subclasses.
  */
  async _open(_ctx) {
  }
  /**
  * To be overridden by subclasses.
  */
  async _close(_ctx) {
  }
  /**
  * Error handler for errors that are caught by the context.
  * By default, errors are bubbled up to the parent context which is passed to the open method.
  */
  async _catch(err) {
    if (CLOSE_RESOURCE_ON_UNHANDLED_ERROR) {
      try {
        await this.close();
      } catch (doubleErr) {
        throwUnhandledError(doubleErr);
      }
    }
    throw err;
  }
  /**
  * Calls the provided function, opening and closing the resource.
  * NOTE: Consider using `using` instead.
  * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/using
  */
  async use(fn) {
    try {
      await this.open();
      return await fn(this);
    } finally {
      await this.close();
    }
  }
  /**
  * Opens the resource.
  * If the resource is already open, it does nothing.
  * If the resource is in an error state, it throws an error.
  * If the resource is closed, it waits for it to close and then opens it.
  * @param ctx - Context to use for opening the resource. This context will receive errors that are not handled in `_catch`.
  */
  async open(ctx) {
    switch (this.#lifecycleState) {
      case "OPEN":
        return this;
      case "ERROR":
        throw new Error(`Invalid state: ${this.#lifecycleState}`);
      default:
    }
    await this.#closePromise;
    await (this.#openPromise ??= this.#open(ctx));
    return this;
  }
  /**
  * Closes the resource.
  * If the resource is already closed, it does nothing.
  */
  async close(ctx) {
    if (this.#lifecycleState === "CLOSED") {
      return this;
    }
    await this.#openPromise;
    await (this.#closePromise ??= this.#close(ctx));
    return this;
  }
  /**
  * Waits until the resource is open.
  */
  async waitUntilOpen() {
    switch (this.#lifecycleState) {
      case "OPEN":
        return;
      case "ERROR":
        throw new Error(`Invalid state: ${this.#lifecycleState}`);
    }
    if (!this.#openPromise) {
      throw new Error("Resource is not being opened");
    }
    await this.#openPromise;
  }
  async #open(ctx) {
    this.#closePromise = null;
    this.#parentCtx = ctx?.derive({
      name: this.#name
    }) ?? this.#createParentContext();
    this.#internalCtx = this.#createContext(this.#parentCtx);
    await this._open(this.#parentCtx);
    this.#lifecycleState = "OPEN";
  }
  async #close(ctx = Context.default()) {
    this.#openPromise = null;
    await this.#internalCtx.dispose();
    await this._close(ctx);
    this.#internalCtx = this.#createContext();
    this.#lifecycleState = "CLOSED";
  }
  #createContext(attributeParent) {
    return new Context({
      name: this.#name,
      parent: attributeParent,
      onError: (error) => queueMicrotask(async () => {
        try {
          await this._catch(error);
        } catch (err) {
          this.#lifecycleState = "ERROR";
          this.#parentCtx.raise(err);
        }
      })
    });
  }
  #createParentContext() {
    return new Context({
      name: this.#name
    });
  }
};
var openInContext = async (ctx, resource) => {
  await resource.open?.(ctx);
  ctx.onDispose(() => resource.close?.());
  return resource;
};

// src/trace-context.ts
var TRACE_SPAN_ATTRIBUTE = "dxos.trace-span";
var ContextRpcCodec = class {
  /**
  * Read the W3C trace context from a DXOS `Context` for an outgoing RPC.
  *
  * @returns `TraceContextData` to attach to the wire message, or `undefined`
  *          if the context has no active trace.
  */
  static encode(ctx) {
    const traceCtx = ctx.getAttribute(TRACE_SPAN_ATTRIBUTE);
    if (traceCtx == null || typeof traceCtx.traceparent !== "string") {
      return void 0;
    }
    return traceCtx;
  }
  /**
  * Reconstruct a DXOS `Context` from W3C trace context received in an
  * incoming RPC request.
  *
  * @returns A `Context` carrying the trace context, or `Context.default()`
  *          if the data is missing/invalid.
  */
  static decode(traceContext) {
    if (typeof traceContext.traceparent !== "string" || traceContext.traceparent.length === 0) {
      return Context.default();
    }
    return new Context({
      attributes: {
        [TRACE_SPAN_ATTRIBUTE]: traceContext
      }
    });
  }
};
export {
  Context,
  ContextDisposedError,
  ContextRpcCodec,
  LifecycleState,
  Resource,
  TRACE_SPAN_ATTRIBUTE,
  cancelWithContext,
  openInContext,
  rejectOnDispose
};
//# sourceMappingURL=index.mjs.map
